import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

const ICON_URL = "https://customer-assets.emergentagent.com/job_pay-next-gen/artifacts/1oy2wqev_ICON.png";
const SPLASH_URL = "https://customer-assets.emergentagent.com/job_pay-next-gen/artifacts/ikoslndy_Splash%20ellipse.png";

function scrollToSection(href) {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function HeroSection() {
  const canvasRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const init = () => {
      resize();
      const count = Math.min(80, Math.floor((canvas.width * canvas.height) / 12000));
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 1.5 + 0.5,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 140) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0,255,0,${0.12 * (1 - dist / 140)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
        const p = particles[i];
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,255,0,0.55)';
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
      }

      animId = requestAnimationFrame(draw);
    };

    init();
    draw();
    window.addEventListener('resize', init, { passive: true });
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', init);
    };
  }, []);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative h-screen min-h-[700px] flex items-center justify-center overflow-hidden bg-black"
      style={{ scrollMarginTop: '0px' }}
    >
      {/* Particle canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ opacity: 0.8 }}
      />

      {/* Grid overlay */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,255,0,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,255,0,0.025) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Radial glow center */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0,255,0,0.06) 0%, transparent 70%)',
        }}
      />

      {/* Splash ellipse image */}
      <div className="absolute inset-0 flex items-center justify-center opacity-10">
        <img src={SPLASH_URL} alt="" className="w-full max-w-3xl object-contain" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        {/* Icon */}
        <div
          className="flex justify-center mb-8"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0) scale(1)' : 'translateY(-20px) scale(0.8)',
            transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          <div className="relative">
            <div
              className="absolute inset-0 rounded-full"
              style={{ background: 'radial-gradient(circle, rgba(0,255,0,0.3) 0%, transparent 70%)', filter: 'blur(20px)' }}
            />
            <img
              src={ICON_URL}
              alt="NexGenPay"
              className="relative h-24 w-24 object-contain"
              style={{ animation: 'float 4s ease-in-out infinite', filter: 'drop-shadow(0 0 20px rgba(0,255,0,0.4))' }}
            />
          </div>
        </div>

        {/* Eyebrow */}
        <p
          className="text-[#00FF00] font-mono text-xs uppercase tracking-[0.5em] mb-6"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.7s ease 0.2s',
          }}
        >
          Unified Four-Rail Payments Platform &bull; Community Banking
        </p>

        {/* Main headline */}
        <h1
          className="text-5xl sm:text-6xl lg:text-7xl font-black uppercase text-white leading-none mb-4"
          style={{
            fontFamily: 'Unbounded, sans-serif',
            letterSpacing: '-0.02em',
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease 0.3s',
          }}
        >
          One Integration.
          <br />
          <span style={{ color: '#00FF00', textShadow: '0 0 40px rgba(0,255,0,0.4)' }}>
            Every Rail.
          </span>
        </h1>

        {/* Tagline */}
        <p
          className="text-[#00FF00] font-mono text-xs uppercase tracking-[0.3em] mb-4"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.7s ease 0.5s',
          }}
        >
          White-Labeled to Your Brand
        </p>

        {/* Sub-headline */}
        <p
          className="text-lg sm:text-xl font-bold text-white max-w-xl mx-auto mb-4 leading-snug"
          style={{
            fontFamily: 'Manrope, sans-serif',
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.7s ease 0.58s',
          }}
        >
          A fully branded{' '}
          <span style={{ color: '#00FF00' }}>mobile wallet.</span>
        </p>

        {/* Subheadline */}
        <p
          className="text-base text-[#A3A3A3] max-w-2xl mx-auto mb-10 leading-relaxed"
          style={{
            fontFamily: 'Manrope, sans-serif',
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.7s ease 0.66s',
          }}
        >
          NexGenPay is the only platform that delivers FedNow, RTP, Stablecoin, and Tokenized Deposits through a single bank-branded wallet. One integration replaces the entire multi-vendor stack. Bank-owned. Examiner-ready.
        </p>

        {/* CTA Buttons */}
        <div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.7s ease 0.8s',
          }}
        >
          <button
            onClick={() => scrollToSection('#demo')}
            data-testid="hero-request-demo-btn"
            className="btn-neon text-sm"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            Request a Demo
          </button>
          <button
            onClick={() => scrollToSection('#solution')}
            data-testid="hero-explore-btn"
            className="btn-outline-neon text-sm"
            style={{ fontFamily: 'Unbounded, sans-serif' }}
          >
            Explore the Platform
          </button>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#A3A3A3]"
        style={{
          opacity: mounted ? 1 : 0,
          transition: 'opacity 1s ease 1.2s',
        }}
      >
        <span className="text-xs uppercase tracking-widest font-mono">Scroll</span>
        <ChevronDown size={16} className="animate-bounce text-[#00FF00]" />
      </div>

      {/* Bottom gradient fade */}
      <div
        className="absolute bottom-0 left-0 right-0 h-32"
        style={{ background: 'linear-gradient(to bottom, transparent, #000)' }}
      />
    </section>
  );
}
